// Labelled text input. Plan 24.
//
// The overwhelming majority of inputs across the app shared the same
// className shape:
//   `w-full px-6 py-4 rounded-2xl border border-stone-100 bg-white
//    focus:ring-2 focus:ring-stone-900/10 transition-all`
// With a smaller 5x3 variant in dense modals. This primitive ships both
// densities behind `density="comfortable" | "compact"`.
//
// Integrates with react-hook-form via the `register` prop:
//   <FormInput label="Email" {...form.register('email', { required: true })} />
// RHF's register returns `{ name, ref, onChange, onBlur }`; we spread
// those onto the `<input>` and wrap the ref via forwardRef so callers
// can still grab the element directly.

import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../utils/cn';

export type InputDensity = 'comfortable' | 'compact';

export interface FormInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Override the auto-generated id (e.g. when binding a sibling `<label>`). */
  id?: string;
  density?: InputDensity;
  /** Class applied to the wrapping container. */
  containerClassName?: string;
}

export const INPUT_BASE_CLASS =
  'w-full rounded-2xl border bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10 transition-all disabled:bg-stone-100 disabled:text-stone-400';

export const INPUT_DENSITY: Record<InputDensity, string> = {
  comfortable: 'px-6 py-4 text-base',
  compact: 'px-5 py-3 text-sm',
};

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(function FormInput(
  { label, hint, error, id, density = 'comfortable', className, containerClassName, ...rest },
  ref
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const describedById = hint || error ? `${inputId}-desc` : undefined;

  return (
    <div className={cn('space-y-1.5', containerClassName)}>
      {label && (
        <label
          htmlFor={inputId}
          className="text-xs font-bold uppercase text-stone-400 ml-1 tracking-wider block"
        >
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedById}
        className={cn(
          INPUT_BASE_CLASS,
          INPUT_DENSITY[density],
          error ? 'border-red-200' : 'border-stone-100',
          className
        )}
        {...rest}
      />
      {(hint || error) && (
        <p
          id={describedById}
          className={cn('text-xs ml-1', error ? 'text-red-600' : 'text-stone-500')}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
});
